import { Input, ISelectOption, Loading, Select } from '@aon/aon-tws-common-ui';
import { IHierarchyVm, INodeSelectionCriteria, SelectHierarchy } from '@aon/tws-common-ui-hierarchy';
import { INodeVm } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import * as React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '../../appStore';
import ContentConstants from '../../contentConstants';
import { AudienceSourceType, IDisplayLogicCondition } from '../../models';
import {
	fetchHierarchy,
	fetchNodAncestors,
	fetchNode,
	hierarchyChange,
	selectHierarchyCriteria,
	toggleNode,
} from '../../store/BranchingLogicHierarchySlice';
import { useBranchingLogicHierarchies } from '../../utilities/hooks';
import { ConditionText, ErrorContainer, LoadingWrapper } from '../common';

export interface IHierarchyBranchingLogicProps {
	audienceSourceType: AudienceSourceType;
	condition: IDisplayLogicCondition;
	branchingLogicIndex: number;
	conditionMessages: string[];
	valueMessages: string[];
}

const SelectConditionContainer = styled.div`
	display: inline-grid;
`;
SelectConditionContainer.displayName = 'SelectConditionContainer';
styled.div.attrs({
	className: 'col-11',
})`
	float: left;
	width: calc(100% - 100px);
`;
const ValueMessages = styled(ErrorContainer)`
	padding: 0;
	margin-bottom: 0;
	margin-left: 20px;
	margin-top: 20px;
`;
ValueMessages.displayName = 'ValueMessages';

export const HierarchyBranchingLogic = ({
	audienceSourceType,
	conditionMessages,
	valueMessages,
	branchingLogicIndex,
	condition: { conditionSourceId: selectedHierarchyId, hierarchy, index: conditionIndex, displayLogicValues },
}: IHierarchyBranchingLogicProps) => {
	const dispatch = useAppDispatch();

	const { selectableHierarchies } = useBranchingLogicHierarchies();

	const selectedHierarchyOption =
		selectedHierarchyId && Array.isArray(selectableHierarchies)
			? selectableHierarchies.find((h) => h.name === selectedHierarchyId)
			: undefined;

	useEffect(() => {
		// When initializing a condition based on a hierarchy source, preset the selection to the given hierarchy
		if (audienceSourceType === AudienceSourceType.Hierarchy && !selectedHierarchyId && Array.isArray(selectableHierarchies)) {
			dispatch(
				hierarchyChange({
					conditionIndex,
					branchingLogicIndex,
					hierarchyMetadataId: selectableHierarchies[0].name,
					hierarchyName: selectableHierarchies[0].displayText,
				}),
			);
		}
	}, [dispatch, audienceSourceType, selectedHierarchyId, selectableHierarchies, branchingLogicIndex, conditionIndex]);

	useEffect(() => {
		if (selectedHierarchyId && !hierarchy) {
			dispatch(
				fetchHierarchy({
					conditionIndex: conditionIndex,
					branchingLogicIndex: branchingLogicIndex,
					hierarchyMetadataId: selectedHierarchyId,
				}),
			);
		}
	}, [dispatch, selectedHierarchyId, conditionIndex, hierarchy, branchingLogicIndex]);

	if (!selectableHierarchies || selectableHierarchies.isLoading) {
		return (
			<LoadingWrapper>
				<Loading color='light-blue' height='70px' width='70px' />
			</LoadingWrapper>
		);
	}

	const renderHierarchyList = selectableHierarchies.couldNotFetchData ? (
		<Input readonly={true} disabled={true} value='N/A' />
	) : (
		<Select
			selectSize='xlg'
			displayText={ContentConstants.DISPLAY_LOGIC_HIERARCHY_DROPDOWN_TEXT}
			onApply={(sel) => onApplyHierarchy(sel as string, selectableHierarchies)}
			selectedValue={selectedHierarchyId ?? ''}
			items={selectableHierarchies}
			message={conditionMessages}
			error={conditionMessages.length !== 0}
		/>
	);

	return (
		<div>
			<div className='row'>
				<ConditionText className='tws-condition-text col-3'>If survey taker is part of </ConditionText>
				<SelectConditionContainer className='tws-hierarchy-node-selector col-8'>
					{audienceSourceType === AudienceSourceType.Hierarchy ? (
						<Input readonly={true} value={selectedHierarchyOption?.displayText ?? ''} />
					) : (
						renderHierarchyList
					)}
				</SelectConditionContainer>
			</div>
			<ValueMessages>{valueMessages.map((m: any) => m)}</ValueMessages>
			{getHierarchyComponent()}
		</div>
	);

	function getHierarchyComponent() {
		// If no hierarchy info is available, abandon rendering.
		if (!hierarchy || (!hierarchy.isLoading && hierarchy.couldNotFetchData)) {
			return;
		}

		if (hierarchy && hierarchy.isLoading) {
			return (
				<LoadingWrapper>
					<Loading color='light-blue' height='70px' width='70px' />
				</LoadingWrapper>
			);
		}

		const defaultSelectionCriteria = { descendantsIncluded: {}, singleIncluded: {} };
		const selectionCriteria = displayLogicValues[0]?.valueText;

		return (
			<SelectHierarchy
				{...hierarchy}
				isLoading={false}
				enableDismiss={true}
				selectionCriteria={selectionCriteria ? JSON.parse(selectionCriteria) : defaultSelectionCriteria}
				onChangeSelectionCriteria={(criteria) => handleHierarchySelectionCriteria(criteria, conditionIndex)}
				toggleNode={toggleHierarchyNode}
				fetchNodAncestors={fetchHierarchyNodAncestors}
			/>
		);
	}

	function handleHierarchyChange(hierarchyMetadataId: string, hierarchyName: string): void {
		dispatch(
			hierarchyChange({
				conditionIndex,
				branchingLogicIndex,
				hierarchyMetadataId,
				hierarchyName,
			}),
		);
	}

	function handleHierarchySelectionCriteria(selectionCriteria: INodeSelectionCriteria, index: number): void {
		dispatch(selectHierarchyCriteria({ conditionIndex: index, branchingLogicIndex, selectionCriteria }));
	}

	function onApplyHierarchy(hierarchyMetadataId: string, fromHierarchies: ISelectOption[]) {
		const hierarchyName = fromHierarchies.find((h) => h.name === hierarchyMetadataId)?.displayText ?? '';
		handleHierarchyChange(hierarchyMetadataId, hierarchyName);
	}

	function isNodeFullyLoaded(hierarchyVm: IHierarchyVm, nodeId: string) {
		if (!hierarchyVm || !hierarchyVm.nodes) {
			return false;
		}
		const nodeVm: INodeVm = hierarchyVm.nodes[nodeId];
		return nodeVm.fullyLoaded;
	}

	function toggleHierarchyNode(nodeId: string, isExpanded: boolean) {
		dispatch(toggleNode({ conditionIndex: conditionIndex, branchingLogicIndex, nodeId }));

		if (!isExpanded || !isNodeFullyLoaded(hierarchy as IHierarchyVm, nodeId)) {
			dispatch(fetchNode({ conditionIndex: conditionIndex, branchingLogicIndex, nodeId, expandPath: false }));
		}
	}

	function fetchHierarchyNodAncestors(hierarchyNodeIds: string[]) {
		dispatch(fetchNodAncestors({ conditionIndex: conditionIndex, branchingLogicIndex, hierarchyNodeIds }));
	}
};
